import styled from "@emotion/styled";
import { Column, Row } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import { useStores } from "@src/stores";
import React from "react";
import BN from "@src/utils/BN";
import useWindowSize from "@src/hooks/useWindowSize";
import { IMarket } from "@src/constants";
import SmallDropdown from "@components/SmallDropdown";
import functionality from "@src/assets/icons/functionality.svg";
import useCase from "@src/assets/icons/useCase.svg";
import mechanics from "@src/assets/icons/mechanics.svg";
import TokensPreview from "@screens/DashboardHome/MarketCard/TokensPreview";
import Skeleton from "react-loading-skeleton";

interface IProps {
  market: IMarket;
}

interface MarketCardRootProps {
  borderColor: string;
}

const MarketCardRoot = styled.div<MarketCardRootProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.card.background};
  overflow: hidden;
  padding: 20px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: ${({ borderColor }) => borderColor};
    border-radius: 0;
  }
`;

const StatsCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.primary50};
  padding: 16px 16px 20px 16px;
  border-radius: 16px;
`;
const StatsContainer = styled.div`
  display: grid;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  gap: 12px;
`;
const ExtraStatsContainer = styled.div`
  display: grid;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  gap: 12px;
`;
const Icon = styled.img`
  width: 32px;
  height: 32px;
  @media (min-width: 768px) {
    width: 64px;
    height: 64px;
  }
`;
const promisesUpdateSpeed = 100;

const MarketInfo: React.FC<IProps> = ({ market }) => {
  const { width } = useWindowSize();
  const { lendStore } = useStores();

  const [totalSupply, setTotalSupply] = React.useState(new BN(-2));
  const fetchTotalSupply = async () => {
    if (totalSupply.eq(-2)) {
      setTotalSupply(await lendStore.getTotalSupply(market.address));
    }
    while (totalSupply.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalSupply(await lendStore.getTotalSupply(market.address));
    }
  };

  const [totalBorrow, setTotalBorrow] = React.useState(new BN(-2));
  const fetchTotalBorrow = async () => {
    if (totalBorrow.eq(-2)) {
      setTotalBorrow(await lendStore.getTotalBorrow(market.address));
    }
    while (totalBorrow.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalBorrow(await lendStore.getTotalBorrow(market.address));
    }
  };

  const [totalAccountSupply, setTotalAccountSupply] = React.useState(
    new BN(-2)
  );
  const fetchTotalAccountSupply = async () => {
    if (totalAccountSupply.eq(-2)) {
      setTotalAccountSupply(
        await lendStore.getTotalAccountSupply(market.address)
      );
    }
    while (totalAccountSupply.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalAccountSupply(
        await lendStore.getTotalAccountSupply(market.address)
      );
    }
  };

  const [totalAccountBorrow, setTotalAccountBorrow] = React.useState(
    new BN(-2)
  );
  const fetchTotalAccountBorrow = async () => {
    if (totalAccountBorrow.eq(-2)) {
      setTotalAccountBorrow(
        await lendStore.getTotalAccountBorrow(market.address)
      );
    }
    while (totalAccountBorrow.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalAccountBorrow(
        await lendStore.getTotalAccountBorrow(market.address)
      );
    }
  };

  const [maxSupplyAPY, setMaxSupplyAPY] = React.useState(new BN(-2));
  const fetchMaxSupplyAPY = async () => {
    if (maxSupplyAPY.eq(-2)) {
      setMaxSupplyAPY(await lendStore.getMaxSupplyAPY(market.address));
    }
    while (maxSupplyAPY.isZero()) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setMaxSupplyAPY(await lendStore.getMaxSupplyAPY(market.address));
    }
  };

  const [health, setHealth] = React.useState(new BN(-2));
  const fetchHealth = async () => {
    if (health.eq(-2)) {
      setHealth(await lendStore.getHealthAsync(market.address));
    }
    while (health.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setHealth(await lendStore.getHealthAsync(market.address));
    }
  };

  Promise.all([
    fetchTotalSupply(),
    fetchTotalBorrow(),
    fetchTotalAccountSupply(),
    fetchTotalAccountBorrow(),
    fetchMaxSupplyAPY(),
    fetchHealth()
  ]);

  const mainStats = [
    {
      title: "Total Supplied ",
      value: `$ ${totalSupply.toFormat(2)} `,
      loading: totalSupply.lte(0)
    },
    {
      title: "Total Borrowed",
      value: `$ ${totalBorrow.toFormat(2)} `,
      loading: totalBorrow.lte(0)
    },
    {
      title: "Max APY",
      value: maxSupplyAPY.gt(100) ? "100+ %" : `${maxSupplyAPY.toFormat(2)} %`
    }
  ];
  const extraStats = [
    {
      title: "Functionality",
      icon: functionality,
      value: market.functionality
    },
    {
      title: "Use Case",
      value: market.useCases,
      icon: useCase
    },
    {
      title: "Mechanics",
      value: market.mechanics,
      icon: mechanics
    }
  ];
  return (
    <MarketCardRoot borderColor={market.color}>
      <Row>
        {width != null && width >= 760 ? (
          <>
            <Icon src={market.icon} alt="market-icon" />
            <SizedBox width={16} />
            <Column>
              <Row alignItems="center">
                <Text fitContent size="bigger" weight={500}>
                  {market.name}
                </Text>
                <SizedBox width={24} />
                <TokensPreview poolAddress={market.address} />
              </Row>
              <Text type="secondary" size="medium">
                {market.description}
              </Text>
            </Column>
          </>
        ) : (
          <Column>
            <Row>
              <Icon src={market.icon} alt="market-icon" />
              <SizedBox width={16} />
              <Text fitContent size="bigger" weight={500}>
                {market.name}
              </Text>
            </Row>
            <SizedBox height={16} />
            <TokensPreview poolAddress={market.address} />
            <SizedBox height={16} />
            <Text type="secondary" size="medium">
              {market.description}
            </Text>
          </Column>
        )}
      </Row>
      <SizedBox height={20} />
      <StatsContainer>
        {mainStats.map(({ title, value, loading }, index) => (
          <StatsCard>
            <Text type="secondary" size="medium" weight={500}>
              {title}
            </Text>
            {loading ? (
              <Skeleton height={24} width={64} />
            ) : (
              <Text size="medium+" weight={500}>
                {value}
              </Text>
            )}
          </StatsCard>
        ))}
      </StatsContainer>
      <SizedBox height={16} />
      <SmallDropdown
        key={`more-details-${market.address}`}
        title="More details"
      >
        <ExtraStatsContainer>
          {extraStats.map(({ title, value, icon }, index) => (
            <StatsCard>
              <Row>
                <img src={icon} alt="icon" />
                <SizedBox width={8} />
                <Text size="medium+" weight={500}>
                  {title}
                </Text>
              </Row>
              <SizedBox height={8} />
              <Text
                type="secondary"
                size="small"
                style={{ whiteSpace: "pre-line" }}
              >
                {value}
              </Text>
            </StatsCard>
          ))}
        </ExtraStatsContainer>
      </SmallDropdown>
    </MarketCardRoot>
  );
};

export default MarketInfo;
